import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaEnvelope, FaPhone, FaFlag, FaMapMarkerAlt, FaBriefcase, FaGraduationCap, FaBullseye, FaUserTag, FaClock, FaFileUpload, FaComment } from 'react-icons/fa';
import './Diagnostic.css';
import { displayMessage } from '../utils/i18nDisplay';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const NATIONALITE_OPTIONS = [
  { value: 'marocaine', key: 'marocaine' },
  { value: 'francaise', key: 'francaise' },
  { value: 'espagnole', key: 'espagnole' },
  { value: 'autre', key: 'autre' }
];
const SITUATION_OPTIONS = [
  { value: 'etudiant', key: 'etudiant' },
  { value: 'salarie', key: 'salarie' },
  { value: 'independant', key: 'independant' },
  { value: 'chomeur', key: 'chomeur' },
  { value: 'retraite', key: 'retraite' }
];
const NIVEAU_OPTIONS = [
  { value: 'bac', key: 'bac' },
  { value: 'bac+2', key: 'bac2' },
  { value: 'bac+3', key: 'bac3' },
  { value: 'bac+4', key: 'bac4' },
  { value: 'bac+5', key: 'bac5' }
];
const OBJECTIF_OPTIONS = [
  { value: 'etudier', key: 'etudier' },
  { value: 'travailler', key: 'travailler' },
  { value: 'creer-entreprise', key: 'creerEntreprise' },
  { value: 'regroupement-familial', key: 'regroupement' },
  { value: 'autre', key: 'autre' }
];
const PROFIL_OPTIONS = [
  { value: 'etudiant', key: 'etudiant' },
  { value: 'travailleur', key: 'travailleur' },
  { value: 'entrepreneur', key: 'entrepreneur' },
  { value: 'membre-famille', key: 'membreFamille' },
  { value: 'autre', key: 'autre' }
];
const DELAI_OPTIONS = [
  { value: 'moins-3-mois', key: 'moins3' },
  { value: '3-6-mois', key: 'troisSix' },
  { value: '6-12-mois', key: 'sixDouze' },
  { value: 'plus-12-mois', key: 'plus12' }
];

const Diagnostic = () => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.resolvedLanguage || i18n.language;

  useEffect(() => {
    const onLanguageChanged = () => {
      setErrors({});
      setSubmitError('');
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => i18n.off('languageChanged', onLanguageChanged);
  }, [i18n]);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    nationalite: '',
    lieuHabitation: '',
    situationProfessionnelle: '',
    niveauEtude: '',
    objectif: '',
    destination: '',
    profil: '',
    delai: '',
    document: null,
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'document') {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'diagnosticPage.errors.nom';
    if (!formData.prenom.trim()) newErrors.prenom = 'diagnosticPage.errors.prenom';
    if (!formData.email.trim()) {
      newErrors.email = 'diagnosticPage.errors.emailRequired';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'diagnosticPage.errors.emailInvalid';
    }
    if (!formData.telephone.trim()) newErrors.telephone = 'diagnosticPage.errors.telephone';
    if (!formData.nationalite) newErrors.nationalite = 'diagnosticPage.errors.nationalite';
    if (!formData.lieuHabitation.trim()) newErrors.lieuHabitation = 'diagnosticPage.errors.lieuHabitation';
    if (!formData.situationProfessionnelle) newErrors.situationProfessionnelle = 'diagnosticPage.errors.situationProfessionnelle';
    if (!formData.niveauEtude) newErrors.niveauEtude = 'diagnosticPage.errors.niveauEtude';
    if (!formData.objectif) newErrors.objectif = 'diagnosticPage.errors.objectif';
    if (!formData.destination.trim()) newErrors.destination = 'diagnosticPage.errors.destination';
    if (!formData.profil) newErrors.profil = 'diagnosticPage.errors.profil';
    if (!formData.delai) newErrors.delai = 'diagnosticPage.errors.delai';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setSending(true);
    const body = new FormData();
    body.append('nom', formData.nom);
    body.append('prenom', formData.prenom);
    body.append('email', formData.email);
    body.append('telephone', formData.telephone);
    body.append('nationalite', formData.nationalite);
    body.append('lieuHabitation', formData.lieuHabitation);
    body.append('situationProfessionnelle', formData.situationProfessionnelle);
    body.append('niveauEtude', formData.niveauEtude);
    body.append('objectif', formData.objectif);
    body.append('destination', formData.destination);
    body.append('profil', formData.profil);
    body.append('delai', formData.delai);
    body.append('message', formData.message);
    if (formData.document) body.append('document', formData.document);

    try {
      const res = await fetch(`${API_BASE}/api/diagnostic`, {
        method: 'POST',
        body
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError('diagnosticPage.submit.error');
        setSending(false);
        return;
      }
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        nationalite: '',
        lieuHabitation: '',
        situationProfessionnelle: '',
        niveauEtude: '',
        objectif: '',
        destination: '',
        profil: '',
        delai: '',
        document: null,
        message: ''
      });
      alert(t('diagnosticPage.submit.success'));
    } catch (err) {
      setSubmitError('diagnosticPage.submit.serverError');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="diagnostic-page" key={langKey}>
      <section className="page-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-3 text-center">
              <img src="/images/logo.jpeg" alt={t('common.altLogo')} className="hero-logo" />
            </div>
            <div className="col-lg-9 text-center">
              <h1 className="page-title">{t('diagnosticPage.hero.title')}</h1>
              <p className="page-subtitle">{t('diagnosticPage.hero.subtitle')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="form-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <form onSubmit={handleSubmit} className="diagnostic-form" noValidate>
                {/* Section 1: Informations personnelles */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaUser className="section-icon" />
                    {t('diagnosticPage.sections.personal')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaUser className="label-icon" />
                        {t('diagnosticPage.labels.nom')}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.nom ? 'is-invalid' : ''}`}
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                      />
                      {errors.nom && <div className="invalid-feedback">{t(errors.nom)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaUser className="label-icon" />
                        {t('diagnosticPage.labels.prenom')}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.prenom ? 'is-invalid' : ''}`}
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                      />
                      {errors.prenom && <div className="invalid-feedback">{errors.prenom}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaEnvelope className="label-icon" />
                        {t('diagnosticPage.labels.email')}
                      </label>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                      {errors.email && <div className="invalid-feedback">{t(errors.email)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaPhone className="label-icon" />
                        {t('diagnosticPage.labels.telephone')}
                      </label>
                      <input
                        type="tel"
                        className={`form-control ${errors.telephone ? 'is-invalid' : ''}`}
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                      />
                      {errors.telephone && <div className="invalid-feedback">{t(errors.telephone)}</div>}
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaFlag className="label-icon" />
                        {t('diagnosticPage.labels.nationalite')}
                      </label>
                      <select
                        className={`form-select ${errors.nationalite ? 'is-invalid' : ''}`}
                        name="nationalite"
                        value={formData.nationalite}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.selectNationality')}</option>
                        {NATIONALITE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.nationalite.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.nationalite && <div className="invalid-feedback">{t(errors.nationalite)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section 2: Situation actuelle */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaMapMarkerAlt className="section-icon" />
                    {t('diagnosticPage.sections.situation')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaMapMarkerAlt className="label-icon" />
                        {t('diagnosticPage.labels.lieuHabitation')}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.lieuHabitation ? 'is-invalid' : ''}`}
                        name="lieuHabitation"
                        value={formData.lieuHabitation}
                        onChange={handleChange}
                        placeholder={t('common.placeholder.cityCountry')}
                        required
                      />
                      {errors.lieuHabitation && <div className="invalid-feedback">{t(errors.lieuHabitation)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaBriefcase className="label-icon" />
                        {t('diagnosticPage.labels.situationProfessionnelle')}
                      </label>
                      <select
                        className={`form-select ${errors.situationProfessionnelle ? 'is-invalid' : ''}`}
                        name="situationProfessionnelle"
                        value={formData.situationProfessionnelle}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.select')}</option>
                        {SITUATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.situation.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.situationProfessionnelle && <div className="invalid-feedback">{errors.situationProfessionnelle}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaGraduationCap className="label-icon" />
                        {t('diagnosticPage.labels.niveauEtude')}
                      </label>
                      <select
                        className={`form-select ${errors.niveauEtude ? 'is-invalid' : ''}`}
                        name="niveauEtude"
                        value={formData.niveauEtude}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.select')}</option>
                        {NIVEAU_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.niveau.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.niveauEtude && <div className="invalid-feedback">{t(errors.niveauEtude)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section 3: Objectif */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaBullseye className="section-icon" />
                    {t('diagnosticPage.sections.objective')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaBullseye className="label-icon" />
                        {t('diagnosticPage.labels.objectif')}
                      </label>
                      <select
                        className={`form-select ${errors.objectif ? 'is-invalid' : ''}`}
                        name="objectif"
                        value={formData.objectif}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.select')}</option>
                        {OBJECTIF_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.objectif.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.objectif && <div className="invalid-feedback">{t(errors.objectif)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        <FaMapMarkerAlt className="label-icon" />
                        {t('diagnosticPage.labels.destination')}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.destination ? 'is-invalid' : ''}`}
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        placeholder={t('common.placeholder.cityCountry')}
                        required
                      />
                      {errors.destination && <div className="invalid-feedback">{t(errors.destination)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section 4: Profil */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaUserTag className="section-icon" />
                    {t('diagnosticPage.sections.profile')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaUserTag className="label-icon" />
                        {t('diagnosticPage.labels.profil')}
                      </label>
                      <select
                        className={`form-select ${errors.profil ? 'is-invalid' : ''}`}
                        name="profil"
                        value={formData.profil}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.select')}</option>
                        {PROFIL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.profil.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.profil && <div className="invalid-feedback">{t(errors.profil)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section 5: Urgence */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaClock className="section-icon" />
                    {t('diagnosticPage.sections.urgency')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaClock className="label-icon" />
                        {t('diagnosticPage.labels.delai')}
                      </label>
                      <select
                        className={`form-select ${errors.delai ? 'is-invalid' : ''}`}
                        name="delai"
                        value={formData.delai}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('common.select')}</option>
                        {DELAI_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`diagnosticPage.options.delai.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.delai && <div className="invalid-feedback">{t(errors.delai)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section 6: Document supplémentaire */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaFileUpload className="section-icon" />
                    {t('diagnosticPage.sections.document')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaFileUpload className="label-icon" />
                        {t('diagnosticPage.labels.upload')}
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="document"
                        onChange={handleChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <small className="form-text text-muted">
                        {t('diagnosticPage.fileFormats')}
                      </small>
                    </div>
                  </div>
                </div>

                {/* Section 7: Message/Commentaires */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaComment className="section-icon" />
                    {t('diagnosticPage.sections.message')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        <FaComment className="label-icon" />
                        {t('diagnosticPage.labels.message')}
                      </label>
                      <textarea
                        className="form-control"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="5"
                        placeholder={t('diagnosticPage.placeholders.message')}
                      ></textarea>
                    </div>
                  </div>
                </div>

                {submitError && (
                  <div className="alert alert-danger" role="alert">
                    {displayMessage(t, submitError)}
                  </div>
                )}
                {/* Bouton de soumission */}
                <div className="form-submit-section">
                  <button type="submit" className="btn btn-submit" disabled={sending}>
                    {sending ? t('diagnosticPage.submit.sending') : t('diagnosticPage.submit.button')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Diagnostic;
